// PBR collection product slider.
//
// Vanilla custom element (no theme dependencies). Provides smooth, page-based
// arrow scrolling over a native scroll-snap rail and toggles the prev/next
// arrows based on scroll position. Card scroll-reveal is handled by the shared
// .pbr-reveal system (pbr-theme.css / pbr-theme.js), not here.

class PbrCollectionSlider extends HTMLElement {
  connectedCallback() {
    // Select by class, not [ref]: the card galleries inside the track are
    // Horizon slideshows whose arrows also carry ref="next"/ref="previous",
    // and the track precedes the rail arrows in the DOM.
    this.track = this.querySelector('.pbr-collection-slider__track');
    this.prevBtn = this.querySelector('.pbr-collection-slider__arrow--prev');
    this.nextBtn = this.querySelector('.pbr-collection-slider__arrow--next');

    // Card galleries are Horizon slideshows marked [mobile-disabled] so they
    // don't hijack rail swipes on touch layouts. Horizon's connectedCallback
    // bakes that state into disabled="true" when connected below 750px and
    // never re-enables it, so keep the attribute in sync across the
    // breakpoint (e.g. tablet rotation).
    this.desktopQuery = window.matchMedia('(min-width: 750px)');
    this.onBreakpointChange = () => this.syncCardGalleries();
    this.desktopQuery.addEventListener('change', this.onBreakpointChange);
    this.syncCardGalleries();

    if (!this.track) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.onScroll = this.throttle(() => this.updateArrows());
    this.onResize = this.throttle(() => this.updateArrows());

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });

    this.prevBtn?.addEventListener('click', () => this.scrollByPage(-1));
    this.nextBtn?.addEventListener('click', () => this.scrollByPage(1));

    // Initial state after layout settles (fonts/images can shift widths).
    requestAnimationFrame(() => this.updateArrows());
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    this.desktopQuery?.removeEventListener('change', this.onBreakpointChange);
  }

  // Only galleries opted into mobile-disabled; galleries hard-disabled by the
  // theme's "product card carousel" setting don't carry the attribute.
  syncCardGalleries() {
    const disabled = String(!this.desktopQuery.matches);
    this.querySelectorAll('slideshow-component[mobile-disabled]').forEach((gallery) => {
      gallery.setAttribute('disabled', disabled);
    });
  }

  // One "page" = the visible width of the rail.
  scrollByPage(direction) {
    if (!this.track) return;
    const amount = this.track.clientWidth * direction;
    this.track.scrollBy({
      left: amount,
      behavior: this.reducedMotion ? 'auto' : 'smooth',
    });
  }

  updateArrows() {
    if (!this.track) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    // Small tolerance so sub-pixel rounding doesn't leave an arrow stuck on.
    const atStart = scrollLeft <= 1;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;
    const overflowing = scrollWidth - clientWidth > 1;

    if (this.prevBtn) this.prevBtn.hidden = atStart;
    if (this.nextBtn) this.nextBtn.hidden = !overflowing || atEnd;
  }

  // rAF-throttled wrapper to keep scroll/resize handlers cheap.
  throttle(fn) {
    let ticking = false;
    return () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        fn();
      });
    };
  }
}

if (!customElements.get('pbr-collection-slider')) {
  customElements.define('pbr-collection-slider', PbrCollectionSlider);
}
